/**
 * Turning an answer into a claim, without retyping it.
 *
 * The moment this exists for: a representative asks whether anything covers
 * moving house, gets told nothing does, and wants that to become a claim. The
 * alternative is copying the reasoning into the claims workbench by hand, which
 * loses the citations — and the citations are the only thing that makes a claim
 * arguable when the school asks where it came from.
 *
 * Three deliberate constraints.
 *
 * It asks for a title and nothing else. Type defaults from what the answer
 * looked like and the relation follows the type; everything else the claim
 * needs is already in the turn. A form that asked for rationale and clauses
 * would be asking the person to retype what they are standing in front of.
 *
 * It says what will happen before it happens — that the claim arrives as a
 * draft nobody owns, and that drafted wording cannot be exported until somebody
 * reads it. A gate discovered later, from a refusal, is experienced as an
 * obstacle; the same gate announced up front is a reassurance.
 *
 * And it does not appear at all until the turn has been recorded. A button that
 * fails because the thing it acts on was never saved is worse than no button.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type AskAnswer } from '../lib/api.js';

const TYPES = [
  { value: 'new', label: 'Something we do not have' },
  { value: 'improve', label: 'Make it better' },
  { value: 'protect', label: 'Keep what we have' },
  { value: 'correct', label: 'Fix a mistake' },
] as const;

export function ClaimFromAnswer({ answer, turnId }: { answer: AskAnswer; turnId: string | undefined }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  /*
   * An answer citing nothing is an answer about a gap, so 'new' is the right
   * default and 'silence' the right relation. An answer citing clauses is
   * usually about changing one of them.
   */
  const gap = answer.citations.length === 0;
  const [type, setType] = useState<string>(gap ? 'new' : 'improve');
  const [made, setMade] = useState<{ ref: string; clauses: number; exportBlocked: boolean } | null>(
    null,
  );

  const raise = useMutation({
    mutationFn: () =>
      api.claimFromTurn(turnId!, {
        title: title.trim(),
        type,
        relation: gap ? 'silence' : 'amends',
      }),
    onSuccess: (result) => {
      setMade(result);
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['ledger-claims'] });
      void queryClient.invalidateQueries({ queryKey: ['suggestions'] });
    },
  });

  if (made) {
    return (
      <p className="mt-2 text-[13px] text-[var(--color-muted)]">
        Raised as <span className="clause-number">{made.ref}</span> — a draft nobody owns yet
        {made.clauses > 0 && `, citing ${made.clauses} clause${made.clauses === 1 ? '' : 's'}`}.
        {made.exportBlocked && ' The drafted wording cannot be exported until somebody reads it.'}
      </p>
    );
  }

  // Nothing to act on until the turn is saved.
  if (!turnId) return null;

  if (!open) {
    return (
      <button
        type="button"
        className="mt-2 text-[13px] text-[var(--color-muted)] underline"
        onClick={() => {
          setOpen(true);
          // Seeded from the question, because the question is usually most of
          // the title and editing beats composing.
          if (!title) setTitle(answer.question.replace(/\?$/, ''));
        }}
      >
        Make this a claim
      </button>
    );
  }

  return (
    <form
      className="mt-2 flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (title.trim().length >= 3) raise.mutate();
      }}
    >
      <input
        className="border-b border-[var(--color-rule)] bg-transparent py-1 text-[14px]"
        aria-label="What the claim is called"
        placeholder="What is the claim?"
        value={title}
        autoFocus
        onChange={(event) => setTitle(event.target.value)}
      />
      <div className="row-flush gap-2">
        <select
          className="border-b border-[var(--color-rule)] bg-transparent py-1 text-[13px]"
          aria-label="What kind of claim"
          value={type}
          onChange={(event) => setType(event.target.value)}
        >
          {TYPES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button type="submit" className="pill" disabled={title.trim().length < 3 || raise.isPending}>
          Raise it
        </button>
        <button type="button" className="pill" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
      <p className="text-[12px] leading-snug text-[var(--color-muted)]">
        {gap
          ? 'This answer cited no clause, so the claim will be recorded as one about a gap.'
          : `The ${answer.citations.length} clause${answer.citations.length === 1 ? '' : 's'} this answer cited will be attached.`}{' '}
        It arrives as a draft with no owner, and the question and answer become its reasoning.
      </p>
    </form>
  );
}
